/**
 * §1.4 Panel 2 — Recent Daily Logs, and deliverable 16's photo strip.
 *
 * Each entry carries date, author, project code, crew count, the weather
 * recorded at entry, the work-performed narrative truncated to two lines, the
 * photo strip (max 5, then "+N more") and a blocker flag.
 *
 * **On the photo tiles.** A.2.10 gives `documents` no thumbnail column and the
 * Dropbox adapter that would render one is Phase 4, so each tile shows the
 * photo's file name rather than a broken image. That is a real state of the
 * system, labelled as such — not a placeholder graphic standing in for data.
 */
import type { DailyLogEntry } from '../lib/api';
import { shortDate } from '../lib/status';

interface Props {
  logs: DailyLogEntry[];
  days: number;
  /** Rendered when the feed is empty, in the words of the calling screen. */
  emptyHint?: string;
  showProject?: boolean;
}

export function DailyLogFeed({ logs, days, emptyHint, showProject = true }: Props): JSX.Element {
  if (logs.length === 0) {
    return (
      <div className="empty-state">
        <p className="text-body font-medium text-text-primary">No daily logs in the last {days} days</p>
        <p className="mt-1 text-body">
          {emptyHint ??
            'Logs appear here as crews file them from the field. Nothing is shown until one is recorded.'}
        </p>
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-2">
      {logs.map((log) => (
        <li key={log.id} className="card p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-body font-semibold text-text-primary">
              {shortDate(log.logDate)}
              {showProject ? (
                <span className="micro-label ml-2 text-text-secondary">{log.projectCode}</span>
              ) : null}
            </p>
            <p className="text-micro text-text-secondary">
              {log.authorName ?? 'Author not recorded'}
              {log.crewCount !== null ? (
                <>
                  {' · '}
                  <span className="numeric">{log.crewCount}</span> crew
                </>
              ) : null}
              {log.hoursWorked !== null ? (
                <>
                  {' · '}
                  <span className="numeric">{log.hoursWorked}</span> h
                </>
              ) : null}
              {weatherText(log.weather) ? ` · ${weatherText(log.weather)}` : ''}
            </p>
          </div>

          {/* §1.4: work performed, truncated to two lines. */}
          <p className="mt-2 line-clamp-2 text-body text-text-primary">{log.workPerformed}</p>

          {log.blockers ? (
            <p className="mt-2 flex items-start gap-2 text-body text-danger">
              <span aria-hidden="true" className="mt-1 block h-2 w-2 flex-none rounded-full bg-danger" />
              <span>
                <span className="micro-label">Blocker</span> {log.blockers}
              </span>
            </p>
          ) : null}

          {log.photos.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {log.photos.map((photo) => (
                <span key={photo.id} className="photo-tile" title={photo.title}>
                  <span className="px-1 leading-tight">{photo.title.slice(0, 14)}</span>
                </span>
              ))}
              {log.photoTotal > log.photos.length ? (
                <span className="text-micro text-text-secondary">
                  +{log.photoTotal - log.photos.length} more
                </span>
              ) : null}
            </div>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function weatherText(weather: DailyLogEntry['weather']): string {
  if (!weather) return '';
  const summary = typeof weather.summary === 'string' ? weather.summary : '';
  const temp = typeof weather.tempF === 'number' ? `${Math.round(weather.tempF)}\u00b0F` : '';
  return [summary, temp].filter(Boolean).join(' ');
}
